import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { LogOut, QrCode, Users, Scan, Search, History, User, Clock, Mail, Shield, Download, Copy, Eye, EyeOff } from 'lucide-react'
import { LoadingSpinner } from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import Footer from '../components/Footer'



interface UserSearchResult {
  id: string
  name: string
  email: string
  code: string
  stamps: number
  createdAt: string
}

interface StampLog {
  id: string
  timestamp: Date
  action: string
  customerCode: string
  customerName: string
  customerEmail: string
  staffName: string
  stampsAdded: number
  previousStamps: number
  newStamps: number
  rewardRedeemed: boolean
}

type TabType = 'dashboard' | 'lookup' | 'marketing' | 'logs'

export default function StaffDashboard() {
  const { staff, signOut } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  
  // User lookup state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  
  // Stamp logs state
  const [stampLogs, setStampLogs] = useState<StampLog[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)

  // Marketing export state
  const [marketingPassword, setMarketingPassword] = useState('')
  const [isMarketingUnlocked, setIsMarketingUnlocked] = useState(false)
  const [marketingFilter, setMarketingFilter] = useState<'true' | 'false' | 'both'>('true')
  const [marketingUsers, setMarketingUsers] = useState<any[]>([])
  const [marketingLoading, setMarketingLoading] = useState(false)
  const [marketingError, setMarketingError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [exportedList, setExportedList] = useState('')

  if (!staff) return null

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success('Signed out successfully')
    } catch (error) {
      toast.error('Failed to sign out')
    }
  }

  const handleUserSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      const staffSession = localStorage.getItem('staffSession')
      if (!staffSession) {
        toast.error('Staff not authenticated')
        return
      }

      const session = JSON.parse(staffSession)
      console.log('Staff session:', session)
      
      // Validate session structure
      const sessionObj = session.session
      if (!sessionObj || !sessionObj.staffId || !sessionObj.loginTime) {
        console.error('Invalid session structure:', session)
        toast.error('Invalid staff session. Please sign in again.')
        navigate('/staff/signin')
        return
      }
      
      const response = await fetch(`https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/staff/users-by-name?name=${encodeURIComponent(searchQuery)}`, {
        method: 'GET',
        headers: {
          'x-staff-session': JSON.stringify(session.session),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'omit'
      })
      
      console.log('Response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('Search results:', data)
        setSearchResults(data.users || [])
      } else {
        const errorText = await response.text()
        console.error('Search failed:', response.status, errorText)
        
        if (response.status === 401) {
          toast.error('Authentication failed - please sign in again')
          navigate('/staff/signin')
        } else if (response.status === 403) {
          toast.error('Access denied - insufficient permissions')
        } else if (response.status >= 500) {
          toast.error('Server error - please try again later')
        } else {
          toast.error(`Failed to search users: ${response.status}`)
        }
        setSearchResults([])
      }
    } catch (error) {
      console.error('User search error:', error)
      toast.error('Failed to search users')
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const loadStampLogs = async () => {
    setIsLoadingLogs(true)
    try {
      const staffSession = localStorage.getItem('staffSession')
      if (!staffSession) {
        toast.error('Staff not authenticated')
        return
      }

      const session = JSON.parse(staffSession)
      console.log('Staff session for logs:', session)
      
      // Validate session structure
      const sessionObj = session.session
      if (!sessionObj || !sessionObj.staffId || !sessionObj.loginTime) {
        console.error('Invalid session structure for logs:', session)
        toast.error('Invalid staff session. Please sign in again.')
        navigate('/staff/signin')
        return
      }
      
      const response = await fetch('https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/staff/logs?limit=50', {
        method: 'GET',
        headers: {
          'x-staff-session': JSON.stringify(session.session),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'omit'
      })
      
      console.log('Logs response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('Logs data:', data)
        setStampLogs(data.logs.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp)
        })))
      } else {
        const errorText = await response.text()
        console.error('Logs failed:', response.status, errorText)
        if (response.status === 401) {
          toast.error('Authentication failed - please sign in again')
          navigate('/staff/signin')
        } else {
          toast.error(`Failed to load stamp logs: ${response.status}`)
        }
      }
    } catch (error) {
      console.error('Stamp logs error:', error)
      toast.error('Failed to load stamp logs')
    } finally {
      setIsLoadingLogs(false)
    }
  }

  const handleUserSelect = (user: UserSearchResult) => {
    navigate(`/staff/scanner?mode=manual&code=${user.code}`)
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Marketing export functions
  const MARKETING_PASSWORD = 'mNP@,74oy,Svfd' // Securely stored password
  
  const validateMarketingPassword = () => {
    if (marketingPassword === MARKETING_PASSWORD) {
      setIsMarketingUnlocked(true)
      setMarketingPassword('') // Clear password from state
      toast.success('Marketing export unlocked!')
    } else {
      toast.error('Invalid password')
      setMarketingPassword('')
    }
  }

  const fetchMarketingUsers = async () => {
    if (!isMarketingUnlocked) return
    
    setMarketingLoading(true)
    setMarketingError('')
    
    try {
      const staffSession = localStorage.getItem('staffSession')
      if (!staffSession) {
        toast.error('Staff not authenticated')
        return
      }

      const session = JSON.parse(staffSession)
      console.log('Staff session for marketing export:', session)
      
      const sessionObj = session.session
      if (!sessionObj || !sessionObj.staffId || !sessionObj.loginTime) {
        toast.error('Invalid staff session. Please sign in again.')
        navigate('/staff/signin')
        return
      }
      
      console.log('Making marketing export request with filter:', marketingFilter)
      
      const response = await fetch('https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/staff/marketing-export', {
        method: 'POST',
        headers: {
          'x-staff-session': JSON.stringify(session.session),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({ filter: marketingFilter })
      })
      
      console.log('Marketing export response status:', response.status)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('Marketing export failed:', response.status, errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }
      
      const data = await response.json()
      console.log('Marketing export data:', data)
      
      setMarketingUsers(data.users || [])
      
      // Generate copy-pasteable list
      const emailList = data.users
        .filter((user: any) => user.email)
        .map((user: any) => user.email)
        .join(', ')
      
      setExportedList(emailList)
      toast.success(`Exported ${data.users.length} users`)
      
    } catch (error: any) {
      console.error('Error fetching marketing users:', error)
      setMarketingError(error.message || 'Failed to fetch marketing users')
      toast.error(`Failed to export users: ${error.message}`)
    } finally {
      setMarketingLoading(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard!')
    } catch (error) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const downloadCSV = () => {
    if (marketingUsers.length === 0) return
    
    const headers = ['Email', 'Name', 'Phone', 'OptInMarketing', 'CreatedAt', 'LastStampAt']
    const csvContent = [
      headers.join(','),
      ...marketingUsers.map(user => [
        user.email || '',
        user.name || '',
        user.phone || '',
        user.optInMarketing || false,
        user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : '',
        user.lastStampAt ? new Date(user.lastStampAt.seconds * 1000).toLocaleDateString() : ''
      ].join(','))
    ].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `marketing-users-${marketingFilter}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Load stamp logs when logs tab is activated
  useEffect(() => {
    if (activeTab === 'logs' && stampLogs.length === 0) {
      loadStampLogs()
    }
  }, [activeTab])

  // Auto-search when query changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'lookup') {
        handleUserSearch()
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, activeTab])

  const renderDashboardTab = () => (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        <h2 className="text-lg font-semibold text-vapour-900 mb-4">
          Quick Actions
        </h2>
        
        <div className="space-y-4">
          <button
            onClick={() => navigate('/staff/scanner')}
            className="w-full bg-gradient-to-r from-vapour-600 to-vapour-700 hover:from-vapour-700 hover:to-vapour-800 text-white font-medium py-4 px-4 rounded-xl transition-all duration-200 flex items-center text-left shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
              <QrCode size={24} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-lg">📱 Camera Scanner</div>
              <div className="text-sm opacity-90">Point camera at customer QR codes</div>
              <div className="text-xs opacity-75 mt-1">✨ Fast & automatic detection</div>
            </div>
          </button>

          <button
            onClick={() => navigate('/staff/scanner?mode=manual')}
            className="w-full bg-gradient-to-r from-dark-200 to-dark-300 hover:from-dark-300 hover:to-dark-400 text-vapour-900 font-medium py-4 px-4 rounded-xl transition-all duration-200 flex items-center text-left shadow-md hover:shadow-lg transform hover:scale-[1.02]"
          >
            <div className="bg-vapour-100 p-3 rounded-lg mr-4">
              <Scan size={24} className="text-vapour-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-lg">⌨️ Manual Entry</div>
              <div className="text-sm opacity-75">Type 5-digit customer codes</div>
              <div className="text-xs opacity-60 mt-1">🔢 For when camera isn't available</div>
            </div>
          </button>

          {staff.role === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-4 px-4 rounded-xl transition-all duration-200 flex items-center text-left shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
                <Users size={24} />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-lg">👑 Admin Dashboard</div>
                <div className="text-sm opacity-90">Manage customers and view reports</div>
                <div className="text-xs opacity-75 mt-1">🔧 Advanced management tools</div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Staff Info */}
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        <h3 className="text-lg font-semibold text-vapour-900 mb-3">
          👤 Your Account
        </h3>
        
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-vapour-600">Name:</span>
            <span className="font-medium text-vapour-800">{staff.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-vapour-600">Email:</span>
            <span className="font-medium text-vapour-800">{staff.username}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-vapour-600">Role:</span>
            <span className={`font-medium capitalize px-2 py-1 rounded-full text-xs ${
              staff.role === 'admin' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {staff.role === 'admin' ? '👑 Admin' : '👨‍💼 Staff'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderLookupTab = () => (
    <div className="space-y-6">
      {/* Search Section */}
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        <h2 className="text-lg font-semibold text-vapour-900 mb-4 flex items-center">
          <Search size={20} className="mr-2" />
          Customer Lookup
        </h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-vapour-700 mb-2">
              Search by customer name
            </label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter customer name..."
              className="w-full px-4 py-3 bg-dark-200 border border-dark-300 rounded-lg text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
            />
          </div>
          
          <p className="text-sm text-vapour-600">
            💡 Perfect for customers who forgot their loyalty card or lost their QR code
          </p>
        </div>
      </div>

      {/* Search Results */}
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        <h3 className="text-lg font-semibold text-vapour-900 mb-4">
          Search Results
        </h3>
        
        {isSearching ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-2 text-vapour-600">Searching...</span>
          </div>
        ) : searchResults.length > 0 ? (
          <div className="space-y-3">
            {searchResults.map((user) => (
              <div
                key={user.id}
                onClick={() => handleUserSelect(user)}
                className="p-4 bg-dark-200 rounded-lg border border-dark-300 hover:border-vapour-600 cursor-pointer transition-all duration-200 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <User size={16} className="text-vapour-600 mr-2" />
                      <h4 className="font-semibold text-vapour-900">{user.name}</h4>
                    </div>
                    <p className="text-sm text-vapour-600 mt-1">{user.email}</p>
                    <div className="flex items-center mt-2 space-x-4">
                      <span className="text-sm font-medium text-vapour-800">
                        Code: {user.code}
                      </span>
                      <span className="text-sm text-vapour-600">
                        Stamps: {user.stamps}/6
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="w-12 h-12 bg-vapour-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-vapour-600">{user.stamps}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : searchQuery ? (
          <div className="text-center py-8 text-vapour-600">
            <Search size={48} className="mx-auto mb-4 opacity-50" />
            <p>No customers found matching "{searchQuery}"</p>
            <p className="text-sm mt-2">Try a different name or check spelling</p>
          </div>
        ) : (
          <div className="text-center py-8 text-vapour-600">
            <Search size={48} className="mx-auto mb-4 opacity-50" />
            <p>Enter a customer name to search</p>
            <p className="text-sm mt-2">Results will appear as you type</p>
          </div>
        )}
      </div>
    </div>
  )

  const renderMarketingTab = () => (
    <div className="space-y-6">
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        <h2 className="text-lg font-semibold text-vapour-900 mb-4 flex items-center">
          <Shield size={20} className="mr-2 text-red-600" />
          Secure Marketing Export
        </h2>

        {!isMarketingUnlocked ? (
          <div className="bg-dark-200 border border-dark-300 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Shield size={20} className="text-vapour-600 mr-3" />
              <h3 className="text-lg font-medium text-vapour-900">Security Verification Required</h3>
            </div>
            <p className="text-vapour-700 mb-4">
              This feature requires additional authentication to access customer marketing data.
            </p>
            <div className="flex items-center space-x-3">
              <div className="relative flex-1 max-w-md">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={marketingPassword}
                  onChange={(e) => setMarketingPassword(e.target.value)}
                  placeholder="Enter marketing access password"
                  className="w-full px-4 py-2 bg-dark-300 border border-dark-400 rounded-lg text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent pr-10"
                  onKeyPress={(e) => e.key === 'Enter' && validateMarketingPassword()}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-vapour-500 hover:text-vapour-700"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button
                onClick={validateMarketingPassword}
                className="px-6 py-2 bg-vapour-600 text-white rounded-lg hover:bg-vapour-700 transition-colors"
              >
                Unlock
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-dark-200 border border-dark-300 rounded-lg p-4">
              <div className="flex items-center">
                <Shield size={16} className="text-green-500 mr-2" />
                <span className="text-vapour-900 font-medium">Marketing export unlocked</span>
                <button
                  onClick={() => setIsMarketingUnlocked(false)}
                  className="ml-auto text-vapour-600 hover:text-vapour-800"
                >
                  Lock
                </button>
              </div>
            </div>

            <div className="bg-dark-200 border border-dark-300 rounded-lg p-6">
              <h3 className="text-lg font-medium text-vapour-900 mb-4">Export Settings</h3>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-vapour-700 mb-3">
                  Marketing Opt-in Filter:
                </label>
                <div className="flex flex-col space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="true"
                      checked={marketingFilter === 'true'}
                      onChange={(e) => setMarketingFilter(e.target.value as 'true')}
                      className="mr-2"
                    />
                    <span className="text-green-400">✅ Opted In (true)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="false"
                      checked={marketingFilter === 'false'}
                      onChange={(e) => setMarketingFilter(e.target.value as 'false')}
                      className="mr-2"
                    />
                    <span className="text-red-400">❌ Opted Out (false)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="both"
                      checked={marketingFilter === 'both'}
                      onChange={(e) => setMarketingFilter(e.target.value as 'both')}
                      className="mr-2"
                    />
                    <span className="text-vapour-700">📋 All Users</span>
                  </label>
                </div>
              </div>

              <button
                onClick={fetchMarketingUsers}
                disabled={marketingLoading}
                className="w-full px-6 py-3 bg-vapour-600 text-white rounded-lg hover:bg-vapour-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {marketingLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} className="inline mr-2" />
                    Export Users
                  </>
                )}
              </button>
            </div>

            {marketingError && (
              <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-4">
                <p className="text-red-400">{marketingError}</p>
              </div>
            )}

            {marketingUsers.length > 0 && (
              <div className="space-y-4">
                <div className="bg-dark-200 border border-dark-300 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-vapour-900 mb-4">
                    📊 Export Results ({marketingUsers.length} users)
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-dark-300 p-3 rounded-lg text-center">
                      <div className="text-xl font-bold text-blue-400">{marketingUsers.length}</div>
                      <div className="text-xs text-vapour-600">Total Users</div>
                    </div>
                    <div className="bg-dark-300 p-3 rounded-lg text-center">
                      <div className="text-xl font-bold text-green-400">
                        {marketingUsers.filter(u => u.email).length}
                      </div>
                      <div className="text-xs text-vapour-600">With Email</div>
                    </div>
                    <div className="bg-dark-300 p-3 rounded-lg text-center">
                      <div className="text-xl font-bold text-purple-400">
                        {marketingUsers.filter(u => u.phone).length}
                      </div>
                      <div className="text-xs text-vapour-600">With Phone</div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-vapour-700 mb-2">
                        📧 Copy-Pasteable Email List:
                      </label>
                      <div className="relative">
                        <textarea
                          value={exportedList}
                          readOnly
                          className="w-full h-24 px-3 py-2 border border-dark-400 rounded-lg bg-dark-300 text-vapour-900 font-mono text-xs"
                          placeholder="Email list will appear here..."
                        />
                        <button
                          onClick={() => copyToClipboard(exportedList)}
                          className="absolute top-2 right-2 px-2 py-1 bg-vapour-600 text-white text-xs rounded hover:bg-vapour-700 transition-colors"
                        >
                          <Copy size={12} className="inline mr-1" />
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={downloadCSV}
                        className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Download size={14} className="inline mr-1" />
                        CSV
                      </button>
                      <button
                        onClick={() => copyToClipboard(JSON.stringify(marketingUsers, null, 2))}
                        className="flex-1 px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <Copy size={14} className="inline mr-1" />
                        JSON
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-200 border border-dark-300 rounded-lg p-4">
                  <h4 className="text-md font-medium text-vapour-900 mb-3">👀 Preview (First 3 Users)</h4>
                  <div className="space-y-2">
                    {marketingUsers.slice(0, 3).map((user, index) => (
                      <div key={index} className="p-2 bg-dark-300 rounded text-xs">
                        <div className="font-medium text-vapour-900">{user.email || 'No email'}</div>
                        <div className="text-vapour-600">{user.name || 'No name'} • {user.phone || 'No phone'}</div>
                        <div className={`text-xs ${user.optInMarketing ? 'text-green-400' : 'text-red-400'}`}>
                          {user.optInMarketing ? '✅ Opted In' : '❌ Opted Out'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  const renderLogsTab = () => (
    <div className="space-y-6">
      {/* Logs Header */}
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-vapour-900 flex items-center">
            <History size={20} className="mr-2" />
            Stamp Activity Logs
          </h2>
          <button
            onClick={loadStampLogs}
            disabled={isLoadingLogs}
            className="px-4 py-2 bg-vapour-600 hover:bg-vapour-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            {isLoadingLogs ? <LoadingSpinner size="sm" /> : 'Refresh'}
          </button>
        </div>
        <p className="text-sm text-vapour-600 mt-2">
          Recent stamp additions and reward redemptions
        </p>
      </div>

      {/* Logs List */}
      <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-6">
        {isLoadingLogs ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-2 text-vapour-600">Loading logs...</span>
          </div>
        ) : stampLogs.length > 0 ? (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {stampLogs.map((log) => (
              <div
                key={log.id}
                className="p-4 bg-dark-200 rounded-lg border border-dark-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        log.rewardRedeemed ? 'bg-green-500' : 'bg-blue-500'
                      }`} />
                      <h4 className="font-semibold text-vapour-900">
                        {log.customerName}
                      </h4>
                      <span className="ml-2 text-sm text-vapour-600">
                        ({log.customerCode})
                      </span>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center text-sm">
                        <Clock size={14} className="text-vapour-500 mr-2" />
                        <span className="text-vapour-600">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-sm">
                        <User size={14} className="text-vapour-500 mr-2" />
                        <span className="text-vapour-600">
                          Staff: {log.staffName}
                        </span>
                      </div>
                      
                      <div className="text-sm">
                        {log.rewardRedeemed ? (
                          <span className="text-green-600 font-medium">
                            🎁 Reward Redeemed - Reset to 0 stamps
                          </span>
                        ) : (
                          <span className="text-blue-600">
                            ⭐ Added {log.stampsAdded} stamp{log.stampsAdded !== 1 ? 's' : ''} 
                            ({log.previousStamps} → {log.newStamps})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      log.rewardRedeemed 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {log.rewardRedeemed ? '🎁' : log.newStamps}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-vapour-600">
            <History size={48} className="mx-auto mb-4 opacity-50" />
            <p>No stamp activity logs found</p>
            <p className="text-sm mt-2">Logs will appear here as customers earn stamps</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 shadow-sm border-b border-dark-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-vapour-900">
              Staff Portal
            </h1>
            <p className="text-sm text-vapour-600">
              Welcome, {staff.name}
            </p>
          </div>
          <button
            onClick={handleSignOut}
            className="p-2 text-vapour-600 hover:text-vapour-900"
            aria-label="Sign out"
          >
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="max-w-md mx-auto px-4 pt-4">
        <div className="bg-dark-100 rounded-xl shadow-sm border border-dark-200 p-2">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'dashboard'
                  ? 'bg-vapour-600 text-white shadow-md'
                  : 'text-vapour-600 hover:bg-dark-200'
              }`}
            >
              <QrCode size={16} className="inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('lookup')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
                activeTab === 'lookup'
                  ? 'bg-vapour-600 text-white shadow-md'
                  : 'text-vapour-600 hover:bg-dark-200'
              }`}
            >
              <Search size={14} className="inline mr-1" />
              Lookup
            </button>
            <button
              onClick={() => setActiveTab('marketing')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
                activeTab === 'marketing'
                  ? 'bg-vapour-600 text-white shadow-md'
                  : 'text-vapour-600 hover:bg-dark-200'
              }`}
            >
              <Mail size={14} className="inline mr-1" />
              Marketing
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
                activeTab === 'logs'
                  ? 'bg-vapour-600 text-white shadow-md'
                  : 'text-vapour-600 hover:bg-dark-200'
              }`}
            >
              <History size={14} className="inline mr-1" />
              Logs
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-md mx-auto px-4 py-6">
        {activeTab === 'dashboard' && renderDashboardTab()}
        {activeTab === 'lookup' && renderLookupTab()}
        {activeTab === 'marketing' && renderMarketingTab()}
        {activeTab === 'logs' && renderLogsTab()}
      </div>

      <Footer />
    </div>
  )
}